import { reviewRequestAttributesSchema } from "@/queries/review-request.js";
import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";
import { queryOptions } from "@tanstack/react-query";
import {
    type PageParams,
    createResourceCollectionSelector,
    handleJsonApiError,
    injectPageParams,
} from "jsonapi-zod-query";
import { z } from "zod";
import { zj } from "zod-joda";

export const reviewAttributesSchema = z.object({
    createdAt: zj.zonedDateTime().transform((value) => value.withZoneSameInstant(localZoneId)),
    rating: z.number().int().min(1).max(5),
    comment: z.string(),
    wouldRecommend: z.boolean(),
    publicSharingAllowed: z.boolean(),
    referralOptIn: z.boolean(),
});

const reviewsSelector = createResourceCollectionSelector({
    type: "review",
    attributesSchema: reviewAttributesSchema,
    relationships: {
        request: {
            relationshipType: "one",
            include: {
                type: "review_request",
                attributesSchema: reviewRequestAttributesSchema,
            },
        },
    },
});

export type Review = ReturnType<typeof reviewsSelector>["data"][number];
export type PaginatedReviews = ReturnType<typeof reviewsSelector>;

export type ListReviewsOptions = {
    pageParams?: PageParams;
};

export const createReviewQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListReviewsOptions) =>
        queryOptions({
            queryKey: ["reviews", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/reviews");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return reviewsSelector(await response.json());
            },
        }),
});
